import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { AppConfig } from '../utils/AppConfig';
import LanguageDetector from 'i18next-browser-languagedetector';

// Define interface for locale files to help TypeScript
interface LocaleMessages {
  MGP?: Record<string, string>;
  Auth?: Record<string, string>;
  Countries?: Record<string, string>;
  Achievements?: Record<string, string>;
  GameFormats?: Record<string, string>;
  Headlines?: Record<string, string>;
  LoadingTips?: Record<string, string>;
  default?: any; // For dynamic imports that might have a default export
}

// Initialize i18n instance
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: AppConfig.defaultLocale,
    supportedLngs: AppConfig.locales,
    debug: false, //process.env.NODE_ENV === 'development',
    defaultNS: 'MGP',
    ns: ['MGP', 'Auth', 'Countries', 'Achievements', 'GameFormats', 'Headlines', 'LoadingTips'],
    resources: {},
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    returnNull: false,
  });

// Listen for language changes and update localStorage
i18n.on('languageChanged', (lng) => {
  localStorage.setItem('locale', lng);
});

// Helper function to safely add resources
const safeAddResource = (locale: string, namespace: string, data: any) => {
  if (data && typeof data === 'object') {
    i18n.addResourceBundle(locale, namespace, data, true, true);
  } else {
    i18n.addResourceBundle(locale, namespace, {}, true, true);
  }
};

// Load a locale file and add its resources to i18n
const loadLocale = async (locale: string) => {
  try {
    // Use a more robust approach to load JSON files
    let response;
    try {
      // Try using fetch API first (works in browser environments)
      response = await fetch(`/src/locales/${locale}.json`);
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }
      const data = await response.json() as LocaleMessages;
      
      // Add all namespaces
      safeAddResource(locale, 'MGP', data.MGP);
      safeAddResource(locale, 'Auth', data.Auth);
      safeAddResource(locale, 'Countries', data.Countries);
      safeAddResource(locale, 'Achievements', data.Achievements);
      safeAddResource(locale, 'GameFormats', data.GameFormats);
      safeAddResource(locale, 'Headlines', data.Headlines);
      safeAddResource(locale, 'LoadingTips', data.LoadingTips);
      
      return true;
    } catch (fetchError) {
      // Fallback to dynamic import if fetch fails
      console.log(`Fetch failed for ${locale}, trying dynamic import`, fetchError);
      
      // Dynamic import as fallback
      const importedModule = await import(`../locales/${locale}.json`);
      const data = importedModule.default || importedModule as LocaleMessages;
      
      // Add all namespaces
      safeAddResource(locale, 'MGP', data.MGP);
      safeAddResource(locale, 'Auth', data.Auth);
      safeAddResource(locale, 'Countries', data.Countries);
      safeAddResource(locale, 'Achievements', data.Achievements);
      safeAddResource(locale, 'GameFormats', data.GameFormats);
      safeAddResource(locale, 'Headlines', data.Headlines);
      safeAddResource(locale, 'LoadingTips', data.LoadingTips);
      
      return true;
    }
  } catch (error) {
    console.error(`Failed to load locale: ${locale}`, error);
    return false;
  }
};

// Load all translations
Promise.all(
  AppConfig.locales.map(locale => loadLocale(locale))
).then(() => {
  // Force a language change to refresh translations
  const currentLang = i18n.language;
  i18n.changeLanguage(currentLang);
});

export default i18n;
